import { displayLocale } from './i18n.ts';

/*
 * The geometry a two-axis time series needs, in a `.ts` file of its own.
 *
 * Not beside the component that draws it: the package's test runner strips
 * types from `.ts` and cannot load `.tsx` at all, so arithmetic that lives
 * next to JSX is arithmetic with no test. The same reason `sorting.ts` exists.
 *
 * Everything here is pure. The component turns the result into SVG and adds
 * no numbers of its own.
 */

export interface ChartSeries {
  /** Stable key — also the legend's React key. */
  readonly key: string;
  readonly label: string;
  /** One value per day, same length and order as `days`. */
  readonly values: readonly number[];
  /** Which scale it is read against. Two series on one scale would lie. */
  readonly axis: 'left' | 'right';
  /** Drawn dashed. The screenshot's clicks line, and how a second series
   *  stays distinguishable without relying on colour alone (§8). */
  readonly dashed?: boolean;
  /** A CSS colour, normally a token. */
  readonly colour: string;
}

export interface ChartPoint {
  readonly x: number;
  readonly y: number;
  /** The value this point was drawn from, for the title/tooltip. */
  readonly value: number;
  readonly day: string;
}

export interface ChartSeriesShape {
  readonly key: string;
  readonly label: string;
  readonly colour: string;
  readonly dashed: boolean;
  readonly axis: 'left' | 'right';
  /** The `d` of the polyline. Empty when there is nothing to draw. */
  readonly path: string;
  readonly points: readonly ChartPoint[];
}

export interface ChartAxis {
  /** Tick values, ascending, the last one being the axis maximum. */
  readonly ticks: readonly number[];
  readonly max: number;
  /** Where each tick sits, top-down, in the same order as `ticks`. */
  readonly ys: readonly number[];
}

export interface ChartModel {
  readonly width: number;
  readonly height: number;
  readonly plot: { readonly left: number; readonly right: number; readonly top: number; readonly bottom: number };
  readonly left: ChartAxis;
  readonly right: ChartAxis;
  readonly series: readonly ChartSeriesShape[];
  /** The series in the caller's own order, for the legend. */
  readonly legend: readonly {
    readonly key: string; readonly label: string;
    readonly colour: string; readonly dashed: boolean;
  }[];
  /** The x labels actually drawn, already thinned to fit. */
  readonly xLabels: readonly { readonly x: number; readonly text: string; readonly day: string }[];
  /** True when every value in every series is zero — the caller shows a note
   *  rather than an axis running 0 to 1 that implies a scale nobody has. */
  readonly empty: boolean;
}

/**
 * A rounded axis maximum and its ticks.
 *
 * An axis that ends on the data's own maximum puts the tallest point exactly
 * on the frame and reads as clipped, so the top tick is always at or above it.
 * 1-2-5 steps because those are the intervals people read without doing
 * arithmetic — 0/2/4/6, not 0/1.7/3.4/5.1.
 */
export function axisTicks(max: number, wanted = 4): { readonly ticks: number[]; readonly max: number } {
  if (!Number.isFinite(max) || max <= 0) return { ticks: [0, 1], max: 1 };
  const rough = max / Math.max(1, wanted - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? 10 * magnitude;
  /*
   * One step past the data when the data lands exactly on a tick. A peak
   * drawn on the top gridline reads as clipped — half the marker sits above
   * the frame — and "is that the real maximum or did it run off?" is the one
   * question a chart must never raise. 5 clicks therefore gives 0/2/4/6, and
   * 3 gives 0/1/2/3/4.
   */
  const fitted = Math.ceil(max / step) * step;
  const top = fitted > max ? fitted : fitted + step;
  const ticks: number[] = [];
  // Accumulating `t += step` drifts on decimal steps (0.4 eight times is not
  // 3.2), and a drifted tick prints as 1.2000000000000002.
  for (let i = 0; i * step <= top + step / 1000; i += 1) ticks.push(round(i * step));
  return { ticks, max: round(top) };
}

/** Trailing float noise removed, at a precision no axis label needs beyond. */
function round(n: number): number {
  return Number(n.toFixed(10));
}

/** "29 Jun", in the reader's own language. */
export function shortDay(day: string, locale: string = displayLocale()): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
  } catch {
    return day;
  }
}

/**
 * How many days to skip between x labels so they do not collide.
 *
 * Driven by the width each label needs rather than by a fixed "every 7th":
 * 90 days on a phone and 90 days on a desktop are the same series and must
 * not print the same 13 overlapping labels.
 */
export function labelEvery(count: number, plotWidth: number, perLabel = 62): number {
  if (count <= 1) return 1;
  const fits = Math.max(1, Math.floor(plotWidth / perLabel));
  return Math.max(1, Math.ceil(count / fits));
}

/**
 * Every day from `from` to `to` inclusive, as ISO days.
 *
 * A report answers with the days that had rows, which is right for a table and
 * wrong for a time series: "Last 30 days" holding two days of traffic drew a
 * two-point line across the whole plot, so a quiet month and a busy one looked
 * identical and the gaps between bursts — the thing the chart is for —
 * disappeared. The caller maps its rows onto this and a day with no row plots
 * as the zero it was.
 *
 * Capped: a range of years is not a daily chart, and a caller that asks for
 * one should get a truncated series rather than a browser that stops
 * responding while it lays out 4,000 points.
 */
export const MAX_DAYS = 400;

export function daysBetween(from: string, to: string): readonly string[] {
  const start = new Date(`${from.slice(0, 10)}T00:00:00Z`);
  const end = new Date(`${to.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end < start) return [];
  const out: string[] = [];
  for (let d = start; d <= end && out.length < MAX_DAYS; d = new Date(d.getTime() + 86_400_000)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export function timeSeriesModel(input: {
  readonly days: readonly string[];
  readonly series: readonly ChartSeries[];
  readonly width?: number;
  readonly height?: number;
  readonly locale?: string;
}): ChartModel {
  const width = input.width ?? 960;
  const height = input.height ?? 170;
  /*
   * Margins in real pixels, because the SVG is now drawn at its real size
   * rather than scaled from a viewBox — so these are the same few pixels on
   * every screen, and the type inside can be sized from the theme's own scale
   * instead of a hard-coded px that grew and shrank with the window.
   *
   * Tight (owner, 2026-09-29: "white/blank space khup jast disat ahe"). Left
   * holds the axis name plus up to five digits; right the same; bottom one
   * line of day labels; top half a marker so the highest point is not clipped.
   */
  const plot = { left: 40, right: 44, top: 6, bottom: 20 };
  const innerW = Math.max(1, width - plot.left - plot.right);
  const innerH = Math.max(1, height - plot.top - plot.bottom);
  const days = input.days;

  const maxOf = (axis: 'left' | 'right') =>
    Math.max(
      0,
      ...input.series
        .filter((s) => s.axis === axis)
        .flatMap((s) => s.values.filter((v) => Number.isFinite(v))),
    );

  const l = axisTicks(maxOf('left'));
  const r = axisTicks(maxOf('right'));
  const yOf = (v: number, max: number) => plot.top + innerH - (v / max) * innerH;
  const axis = (a: { ticks: number[]; max: number }): ChartAxis => ({
    ticks: a.ticks,
    max: a.max,
    ys: a.ticks.map((t) => yOf(t, a.max)),
  });

  // One point is a dot, not a line; `days.length - 1` would divide by zero.
  const step = days.length > 1 ? innerW / (days.length - 1) : 0;
  const xOf = (i: number) => (days.length > 1 ? plot.left + i * step : plot.left + innerW / 2);

  /*
   * Solid lines first, dashed ones last.
   *
   * Two series that happen to hold the same values trace the same path, and
   * whichever is drawn second hides the other completely — seen on staging
   * 2026-09-29, where one click and one registration a day drew one red line
   * and a reader would have concluded there were no clicks at all. A dashed
   * line drawn *over* a solid one shows the solid through its gaps, so both
   * survive the collision. Ordering is the whole fix; nothing is offset, so
   * every point still sits where its value says.
   */
  const drawOrder = [...input.series].sort(
    (a, b) => Number(a.dashed ?? false) - Number(b.dashed ?? false),
  );

  const series = drawOrder.map((s): ChartSeriesShape => {
    const max = s.axis === 'left' ? l.max : r.max;
    /*
     * A day the series has no value for breaks the line rather than being
     * drawn through: joining across a gap asserts a figure nobody reported,
     * which is the same rule `Spark` follows. `M` after a gap starts a new
     * run instead of dropping the whole series.
     */
    const points: ChartPoint[] = [];
    const parts: string[] = [];
    let penDown = false;
    days.forEach((day, i) => {
      const v = s.values[i];
      if (v === undefined || !Number.isFinite(v)) { penDown = false; return; }
      const x = xOf(i);
      const y = yOf(v, max);
      points.push({ x, y, value: v, day });
      parts.push(`${penDown ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`);
      penDown = true;
    });
    return {
      key: s.key,
      label: s.label,
      colour: s.colour,
      dashed: s.dashed ?? false,
      axis: s.axis,
      path: parts.join(' '),
      points,
    };
  });

  const every = labelEvery(days.length, innerW);
  const xLabels = days
    .map((day, i) => ({ day, i }))
    .filter(({ i }) => i % every === 0)
    .map(({ day, i }) => ({ x: xOf(i), text: shortDay(day, input.locale), day }));

  return {
    width,
    height,
    plot,
    left: axis(l),
    right: axis(r),
    series,
    /*
     * The legend reads in the order the caller listed the series — clicks
     * then registrations, the order the cards above are in — not in the order
     * they happen to be painted. Paint order is a collision fix and has no
     * business reordering what the reader is told.
     */
    legend: input.series.map((s) => ({
      key: s.key, label: s.label, colour: s.colour, dashed: s.dashed ?? false,
    })),
    xLabels,
    empty: input.series.every((s) => s.values.every((v) => !Number.isFinite(v) || v === 0)),
  };
}

/* ------------------------------------------------------------------ *
 * Grain — day, month or year
 * ------------------------------------------------------------------ */

export type Grain = 'day' | 'month' | 'year';

/**
 * The bucket a day belongs to, as the key the chart plots against.
 *
 * ISO prefixes rather than parsed dates: `2026-09-27` is in `2026-09` and
 * `2026`, and slicing the string cannot slip a day across a boundary the way
 * constructing a local `Date` can (a clock change, or a browser east of
 * Greenwich, and January the 1st lands in the previous year).
 */
export function bucketOf(day: string, grain: Grain): string {
  const iso = day.slice(0, 10);
  if (grain === 'year') return iso.slice(0, 4);
  if (grain === 'month') return iso.slice(0, 7);
  return iso;
}

/**
 * Daily rows rolled up to the chosen grain, keeping the caller's order.
 *
 * Sums, because every series this chart draws is a count of things that
 * happened: a month's clicks are its days' clicks added up. A series that was
 * an average or a rate would need its own arithmetic and must not be passed
 * here — the chart draws clicks and registrations, and both are counts.
 *
 * A bucket with no day in it is not invented. `daysBetween` fills the range
 * before this runs, so a quiet month is present with zeros rather than absent.
 */
export function rollUp<Row extends { readonly day: string }>(
  rows: readonly Row[],
  grain: Grain,
  values: readonly (keyof Row & string)[],
): readonly { readonly day: string; readonly [k: string]: string | number }[] {
  if (grain === 'day') return rows as never;
  const out = new Map<string, Record<string, string | number>>();
  for (const row of rows) {
    const key = bucketOf(row.day, grain);
    const at = out.get(key) ?? { day: key, ...Object.fromEntries(values.map((v) => [v, 0])) };
    for (const v of values) {
      const n = Number(row[v]);
      at[v] = Number(at[v] ?? 0) + (Number.isFinite(n) ? n : 0);
    }
    out.set(key, at);
  }
  return [...out.values()] as never;
}

/** "Sep 2026" for a month bucket, "2026" for a year, the day format otherwise. */
export function bucketLabel(key: string, grain: Grain, locale?: string): string {
  if (grain === 'year') return key;
  if (grain === 'month') {
    const d = new Date(`${key}-01T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return key;
    try {
      return new Intl.DateTimeFormat(locale ?? displayLocale(), {
        month: 'short', year: 'numeric', timeZone: 'UTC',
      }).format(d);
    } catch {
      return key;
    }
  }
  return shortDay(key, locale);
}
